import { createClient } from "@supabase/supabase-js";

const supabaseUrl = "https://yqusxudykdyojjpvwouj.supabase.co";
const supabaseAnonKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlxdXN4dWR5a2R5b2pqcHZ3b3VqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc2NDk3NjcsImV4cCI6MjA2MzIyNTc2N30.2LVLLzlKmfsDuIDkz1kgt4q2bT2uJ395WF-7aKDAVlQ";

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Test function to verify Supabase connection
export const testSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase
      .from("linkedin_agent_traces")
      .select("count")
      .limit(1);

    if (error) {
      console.error("Supabase connection test failed:", error);
      return false;
    }

    console.log("Supabase connection test successful");
    return true;
  } catch (error) {
    console.error("Supabase connection test error:", error);
    return false;
  }
};

// Helper function to get total count of traces for a batch
export const getTracesCountFromSupabase = async (batchId) => {
  try {
    const { count, error } = await supabase
      .from("linkedin_agent_traces")
      .select("*", { count: "exact", head: true })
      .eq("job_batch_id", batchId);

    if (error) {
      console.error("Error getting traces count from Supabase:", error);
      throw error;
    }

    console.log("Total traces count for batch", batchId, ":", count);
    return count || 0;
  } catch (error) {
    console.error("Error in getTracesCountFromSupabase:", error);
    throw error;
  }
};

// Helper function to fetch traces with better error handling
export const fetchTracesFromSupabase = async (
  batchId,
  offset = 0,
  limit = 100
) => {
  try {
    const { data, error } = await supabase
      .from("linkedin_agent_traces")
      .select("job_batch_id, traces, id")
      .eq("job_batch_id", batchId)
      .range(offset, offset + limit - 1) // Fetch limit number of records
      .order("id", { ascending: true });

    if (error) {
      console.error("Error fetching traces from Supabase:", error);
      throw error;
    }

    console.log(
      `Fetched ${data.length} traces from offset ${offset} for batch ${batchId}:`,
      data
    );

    // Transform the data to match the expected format
    const transformedTraces = data.map((row) => {
      try {
        // Since traces are simple strings, wrap each one in an array
        if (typeof row.traces === "string") {
          return [row.traces];
        } else if (Array.isArray(row.traces)) {
          // If already an array, use as is
          return row.traces;
        } else {
          // If it's something else, wrap in array
          return [String(row.traces)];
        }
      } catch (error) {
        console.error("Error processing traces:", error);
        return [row.traces || "Error processing trace"];
      }
    });

    console.log("Transformed traces:", transformedTraces);
    return transformedTraces;
  } catch (error) {
    console.error("Error in fetchTracesFromSupabase:", error);
    throw error;
  }
};

// Helper function to fetch ALL traces for a batch without pagination
export const fetchAllTracesFromSupabase = async (batchId) => {
  try {
    const { data, error } = await supabase
      .from("linkedin_agent_traces")
      .select("job_batch_id, traces, id")
      .eq("job_batch_id", batchId)
      .order("id", { ascending: true });

    if (error) {
      console.error("Error fetching all traces from Supabase:", error);
      throw error;
    }

    console.log(
      `Fetched ALL ${data.length} traces for batch ${batchId}:`,
      data
    );

    // Transform the data to match the expected format
    const transformedTraces = data.map((row) => {
      try {
        // Since traces are simple strings, wrap each one in an array
        if (typeof row.traces === "string") {
          return [row.traces];
        } else if (Array.isArray(row.traces)) {
          // If already an array, use as is
          return row.traces;
        } else {
          // If it's something else, wrap in array
          return [String(row.traces)];
        }
      } catch (error) {
        console.error("Error processing traces:", error);
        return [row.traces || "Error processing trace"];
      }
    });

    console.log("Transformed traces:", transformedTraces);
    return transformedTraces;
  } catch (error) {
    console.error("Error in fetchAllTracesFromSupabase:", error);
    throw error;
  }
};

// Helper function to fetch active pipeline stats from Supabase
export const fetchActivePipelineStatsFromSupabase = async (batchId) => {
  try {
    const { data, error } = await supabase
      .from("linkedin_agent_candidates")
      .select("selected")
      .eq("job_batch_id", batchId);

    if (error) {
      console.error("Error fetching pipeline stats from Supabase:", error);
      throw error;
    }

    console.log("Raw candidates data from Supabase:", data);

    // Calculate stats
    const totalCandidates = data.length;
    const selectedCandidates = data.filter(
      (candidate) => candidate.selected === true
    ).length;

    const stats = {
      candidates_found: selectedCandidates,
      profiles_processed: totalCandidates,
    };

    console.log("Calculated pipeline stats:", stats);
    return stats;
  } catch (error) {
    console.error("Error in fetchActivePipelineStatsFromSupabase:", error);
    throw error;
  }
};

// Helper function to fetch job batches from Supabase
export const fetchJobBatchesFromSupabase = async (userId) => {
  try {
    let query = supabase
      .from("linkedin_agent_job_batches")
      .select("id, job_batch_name, user_id")
      .order("id", { ascending: false });

    // If userId is the special admin user, fetch all batches
    if (userId === "e2226220-2050-437b-9b12-a5d4d220ec97") {
      console.log("Admin user detected, fetching all job batches");
    } else {
      // Otherwise, filter by user_id
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching job batches from Supabase:", error);
      throw error;
    }

    console.log("Job batches from Supabase:", data);

    // Transform the data to match the expected format
    const transformedBatches = {
      job_batches: data.map((batch) => ({
        id: batch.id,
        job_batch_name: batch.job_batch_name,
        user_id: batch.user_id,
      })),
    };

    console.log("Transformed job batches:", transformedBatches);
    return transformedBatches;
  } catch (error) {
    console.error("Error in fetchJobBatchesFromSupabase:", error);
    throw error;
  }
};
